import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { useAuth } from '../contexts/AuthContext';
import { getApiUrl } from '../config/environment';
import { toast } from 'sonner';
import BackButton from '../components/BackButton';
import {
  Award, TrendingUp, Users, Calendar, Euro, Shield,
  X, ArrowRight, Check, Upload, Camera, CreditCard,
  Loader2, CreditCard as CardIcon, Building2
} from 'lucide-react';

const API = getApiUrl();

const SPECIALTIES_SUGGESTIONS = [
  'Leadership', 'Management', 'Communication', 'Gestion du stress',
  'Prise de parole', 'Intelligence émotionnelle', 'Gestion de conflits',
  'Transition de carrière', 'Performance', 'Organisation',
  'Coaching d\'équipe', 'Développement personnel',
];

const BENEFITS = [
  { icon: Users, title: 'Clients qualifiés', desc: 'Des managers en recherche active de coaching.' },
  { icon: Calendar, title: 'Agenda intégré', desc: 'Créneaux et réservations automatisés.' },
  { icon: Euro, title: 'Paiement sécurisé', desc: 'Transactions via Stripe, zéro friction.' },
  { icon: TrendingUp, title: 'Rapports IA', desc: 'Analyses post-session pour vos clients.' },
  { icon: Shield, title: 'Visio intégrée', desc: 'Sessions Jitsi, sans outil externe.' },
  { icon: Award, title: 'Crédibilité', desc: 'Profil premium, notations visibles.' },
];

export default function CoachOnboarding() {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [step, setStep] = useState('landing');
  const [loading, setLoading] = useState(true);

  const [form, setForm] = useState({
    display_name: '', bio: '', specialties: [], hourly_rate: 80,
    session_duration: 60, phone: '', linkedin_url: '', certifications: [],
    certification_number: '',
    payment_method: '', // 'bank_transfer' or 'paypal'
    iban: '', bic: '', account_holder: '',
    paypal_email: '',
  });
  const [newSpecialty, setNewSpecialty] = useState('');
  const [newCert, setNewCert] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Document files (stored locally before submit)
  const [docFiles, setDocFiles] = useState({ profile_photo: null, id_document: null, coach_certificate: null, business_proof: null });

  useEffect(() => {
    const check = async () => {
      if (!token) { setLoading(false); return; }
      try {
        const res = await fetch(`${API}/coach/profile`, { headers: { Authorization: `Bearer ${token}` } });
        const data = await res.json();
        if (data.has_profile) navigate('/coach/dashboard');
      } catch (err) { /* ignore */ }
      setLoading(false);
    };
    check();
  }, [token, navigate]);

  const addSpecialty = (s) => {
    if (s && !form.specialties.includes(s)) {
      setForm({ ...form, specialties: [...form.specialties, s] });
    }
  };

  // RIB document (optional, for bank transfer)
  const [ribFile, setRibFile] = useState(null);
  const [plansRange, setPlansRange] = useState({ min: null, max: null });

  // Fetch subscription plans to display dynamic price range (source: Stripe via backend)
  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const res = await fetch(`${API}/marketplace/subscription-plans`);
        if (res.ok) {
          const { plans = [] } = await res.json();
          const active = plans.filter(p => p.is_active);
          if (active.length) {
            const prices = active.map(p => Number(p.price_eur) || 0).filter(n => n > 0);
            if (prices.length) setPlansRange({ min: Math.min(...prices), max: Math.max(...prices) });
          }
        }
      } catch (e) { /* silent */ }
    };
    fetchPlans();
  }, []);

  const isPaymentComplete = form.payment_method === 'paypal'
    ? Boolean(form.paypal_email && form.paypal_email.includes('@'))
    : form.payment_method === 'bank_transfer'
      ? Boolean(form.iban && form.account_holder && ribFile)
      : false;

  const isFormComplete = form.display_name && form.bio && form.specialties.length > 0
    && form.hourly_rate && form.certification_number
    && docFiles.profile_photo && docFiles.id_document && docFiles.coach_certificate && docFiles.business_proof
    && isPaymentComplete;

  const handleSubmit = async () => {
    if (!isFormComplete) { toast.error('Veuillez remplir tous les champs et déposer vos 4 justificatifs'); return; }
    setSubmitting(true);
    try {
      // Step 1: Create profile (including payment method)
      const regRes = await fetch(`${API}/coach/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          ...form,
          hourly_rate: Number(form.hourly_rate) || 80,
          session_duration: Number(form.session_duration) || 60,
        }),
      });
      if (!regRes.ok) { const err = await regRes.json(); throw new Error(err.detail || 'Erreur création profil'); }

      // Step 2: Upload all 3 documents + RIB if bank transfer
      const docsToUpload = { ...docFiles };
      if (form.payment_method === 'bank_transfer' && ribFile) {
        docsToUpload.rib_document = ribFile;
      }
      for (const [docType, file] of Object.entries(docsToUpload)) {
        if (!file) continue;
        const upRes = await fetch(`${API}/coach/documents/upload?doc_type=${docType}`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': file.type || 'application/octet-stream',
            // URL-encode filename to stay ASCII-safe (fixes "Failed to fetch" on non-ASCII names)
            'X-Filename': encodeURIComponent(file.name),
          },
          body: file,
        });
        if (!upRes.ok) { const err = await upRes.json(); throw new Error(err.detail || `Erreur upload ${docType}`); }
      }

      toast.success('Profil créé et documents envoyés !');
      setStep('success');
    } catch (err) {
      // Network / CORS errors come through as TypeError ("Failed to fetch")
      const message = err?.message === 'Failed to fetch'
        ? 'Connexion impossible — vérifiez votre réseau puis réessayez.'
        : (err?.message || 'Une erreur est survenue');
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="theme-light min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#D4AF37] animate-spin" />
      </div>
    );
  }

  // === SUCCESS ===
  if (step === 'success') {
    return (
      <div className="theme-light min-h-screen flex items-center justify-center px-4">
        <div className="max-w-md w-full text-center">
          <div className="w-20 h-20 rounded-full bg-green-50 border-2 border-green-200 flex items-center justify-center mx-auto mb-6">
            <Check className="w-10 h-10 text-green-600" />
          </div>
          <h2 className="text-2xl font-display font-medium text-[#111827] mb-3">Dossier envoyé</h2>
          <p className="text-[#4B5563] mb-6 font-body">
            Votre profil et justificatifs ont été transmis. Notre équipe vérifie votre dossier sous 24 à 48 heures.
          </p>
          <div className="bg-[#FEF9E7] border border-[#F5E6A3] rounded-xl p-5 mb-8 text-left">
            <p className="text-[#92710A] text-sm font-medium mb-1">En attente de validation</p>
            <p className="text-[#4B5563] text-sm leading-relaxed">
              Vous recevrez un email dès que votre profil sera approuvé. Vous pourrez alors activer votre visibilité dans l'Espace Coachs.
            </p>
          </div>
          <button data-testid="go-to-coach-dashboard" onClick={() => navigate('/coach/dashboard')}
            className="w-full py-3.5 lt-btn-gold font-display text-sm flex items-center justify-center gap-2">
            Accéder à mon espace coach <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    );
  }

  // === FORM (unified: profile + uploads + business model) ===
  if (step === 'form') {
    const DOC_ITEMS = [
      { key: 'profile_photo', label: 'Photo de profil', icon: Camera, accept: 'image/*', desc: 'Photo professionnelle (JPG, PNG)', required: true },
      { key: 'id_document', label: 'Pièce d\'identité', icon: CreditCard, accept: 'image/*,application/pdf', desc: 'CNI, passeport (JPG, PNG, PDF)', required: true },
      { key: 'coach_certificate', label: 'Certificat coach', icon: Award, accept: 'image/*,application/pdf', desc: 'Diplôme ou certification (JPG, PNG, PDF)', required: true },
      { key: 'business_proof', label: 'Justificatif d\'exercice professionnel', icon: Building2, accept: 'image/*,application/pdf', desc: 'Kbis, SIRENE, SIRET, URSSAF ou équivalent (JPG, PNG, PDF)', required: true },
    ];

    return (
      <div className="theme-light min-h-screen pb-28 sm:pb-12">
        {/* Header */}
        <div className="border-b border-[#E8E5DE] bg-white/80 backdrop-blur-sm sticky top-0 z-20">
          <div className="max-w-2xl mx-auto px-4 sm:px-6 pt-14 sm:pt-5 pb-3">
            <BackButton />
          </div>
        </div>

        <div className="max-w-2xl mx-auto px-4 sm:px-6 pt-8">
          <p className="uppercase text-xs tracking-[0.2em] text-[#92710A] mb-2 font-body font-medium">Inscription coach</p>
          <h1 className="text-2xl sm:text-3xl font-display font-medium text-[#111827] tracking-tight mb-2">
            Créez votre profil
          </h1>
          <p className="text-[#4B5563] font-body mb-8">
            Remplissez vos informations et déposez vos justificatifs pour rejoindre l'Espace Coachs.
          </p>

          {!user && (
            <div className="bg-[#FEF9E7] border border-[#F5E6A3] rounded-xl p-4 mb-6">
              <p className="text-[#92710A] text-sm font-body">
                Connectez-vous pour créer votre profil.{' '}
                <button onClick={() => navigate('/login')} className="underline font-medium">Se connecter</button>
              </p>
            </div>
          )}

          <div className="space-y-8">

            {/* === SECTION 1: Informations === */}
            <section>
              <h2 className="text-sm font-display font-medium text-[#111827] uppercase tracking-wider mb-4 pb-2 border-b border-[#E8E5DE]">
                Informations
              </h2>
              <div className="space-y-5">
                <div>
                  <label className="text-sm font-medium text-[#374151] mb-1.5 block font-body">Nom affiché *</label>
                  <Input data-testid="onboard-display-name" value={form.display_name}
                    onChange={(e) => setForm({ ...form, display_name: e.target.value })}
                    className="bg-white border-[#DDD9D0] text-[#111827] rounded-xl focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37]/20" placeholder="Ex: Marie Dupont" />
                </div>
                <div>
                  <label className="text-sm font-medium text-[#374151] mb-1.5 block font-body">Bio *</label>
                  <Textarea data-testid="onboard-bio" value={form.bio}
                    onChange={(e) => setForm({ ...form, bio: e.target.value })}
                    className="bg-white border-[#DDD9D0] text-[#111827] min-h-[100px] rounded-xl focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37]/20"
                    placeholder="Votre expérience, approche et philosophie de coaching..." />
                </div>
                <div>
                  <label className="text-sm font-medium text-[#374151] mb-1.5 block font-body">Spécialités * — sélectionnées : {form.specialties.length}</label>
                  <div className="flex flex-wrap gap-1.5 mb-2">
                    {form.specialties.map((s, i) => (
                      <Badge key={i} className="bg-[#F5F0E3] text-[#7A5F0A] border border-[#E8DFC8] cursor-pointer hover:bg-red-50 hover:text-red-600 hover:border-red-200 transition-colors"
                        onClick={() => setForm({ ...form, specialties: form.specialties.filter((_, j) => j !== i) })}>
                        {s} <X className="w-3 h-3 ml-1" />
                      </Badge>
                    ))}
                  </div>
                  <div className="flex gap-2 mb-2">
                    <Input value={newSpecialty} onChange={(e) => setNewSpecialty(e.target.value)}
                      className="bg-white border-[#DDD9D0] text-[#111827] rounded-xl" placeholder="Ajouter une spécialité"
                      onKeyDown={(e) => { if (e.key === 'Enter' && newSpecialty.trim()) { e.preventDefault(); addSpecialty(newSpecialty.trim()); setNewSpecialty(''); } }} />
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {SPECIALTIES_SUGGESTIONS.filter(s => !form.specialties.includes(s)).slice(0, 8).map(s => (
                      <button key={s} onClick={() => addSpecialty(s)}
                        className="px-2.5 py-1 rounded-full text-xs bg-[#F8F7F4] border border-[#E8E5DE] text-[#4B5563] hover:text-[#111827] hover:border-[#D4AF37] transition-all font-body">
                        + {s}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-[#374151] mb-1.5 block font-body">Tarif horaire (€) *</label>
                    <Input data-testid="onboard-rate" type="number" value={form.hourly_rate === '' ? '' : form.hourly_rate}
                      onChange={(e) => setForm({ ...form, hourly_rate: e.target.value === '' ? '' : parseFloat(e.target.value) })}
                      className="bg-white border-[#DDD9D0] text-[#111827] rounded-xl" min="0" step="0.5" placeholder="80" />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-[#374151] mb-1.5 block font-body">Durée session (min)</label>
                    <Input data-testid="onboard-duration" type="number" value={form.session_duration === '' ? '' : form.session_duration}
                      onChange={(e) => setForm({ ...form, session_duration: e.target.value === '' ? '' : parseInt(e.target.value) })}
                      className="bg-white border-[#DDD9D0] text-[#111827] rounded-xl" min="15" step="15" placeholder="60" />
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium text-[#374151] mb-1.5 block font-body">Numéro de certification *</label>
                  <Input data-testid="onboard-certification-number" value={form.certification_number}
                    onChange={(e) => setForm({ ...form, certification_number: e.target.value })}
                    className="bg-white border-[#DDD9D0] text-[#111827] rounded-xl" placeholder="Ex: ICF-2024-XXXXX" />
                </div>
                <div>
                  <label className="text-sm font-medium text-[#374151] mb-1.5 block font-body">Certifications (optionnel)</label>
                  <div className="flex flex-wrap gap-1.5 mb-2">
                    {form.certifications.map((c, i) => (
                      <Badge key={i} variant="outline" className="bg-white text-[#374151] border-[#DDD9D0] cursor-pointer hover:bg-red-50 hover:border-red-200"
                        onClick={() => setForm({ ...form, certifications: form.certifications.filter((_, j) => j !== i) })}>
                        {c} <X className="w-3 h-3 ml-1" />
                      </Badge>
                    ))}
                  </div>
                  <Input value={newCert} onChange={(e) => setNewCert(e.target.value)}
                    className="bg-white border-[#DDD9D0] text-[#111827] rounded-xl" placeholder="Ex: ICF PCC, MBTI Certified..."
                    onKeyDown={(e) => { if (e.key === 'Enter' && newCert.trim()) { e.preventDefault(); setForm({ ...form, certifications: [...form.certifications, newCert.trim()] }); setNewCert(''); } }} />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-[#374151] mb-1.5 block font-body">Téléphone</label>
                    <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
                      className="bg-white border-[#DDD9D0] text-[#111827] rounded-xl" placeholder="+33 6 ..." />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-[#374151] mb-1.5 block font-body">LinkedIn</label>
                    <Input value={form.linkedin_url} onChange={(e) => setForm({ ...form, linkedin_url: e.target.value })}
                      className="bg-white border-[#DDD9D0] text-[#111827] rounded-xl" placeholder="https://linkedin.com/in/..." />
                  </div>
                </div>
              </div>
            </section>

            {/* === SECTION 2: Justificatifs === */}
            <section>
              <h2 className="text-sm font-display font-medium text-[#111827] uppercase tracking-wider mb-1 pb-2 border-b border-[#E8E5DE]">
                Justificatifs obligatoires
              </h2>
              <p className="text-[#4B5563] text-xs mb-4 font-body">Ces documents sont nécessaires pour valider votre profil.</p>
              <div className="space-y-3">
                {DOC_ITEMS.map(({ key, label, icon: Icon, accept, desc }) => {
                  const file = docFiles[key];
                  return (
                    <div key={key} className={`bg-white border rounded-xl p-4 transition-all shadow-sm ${file ? 'border-green-300 bg-green-50/30' : 'border-[#DDD9D0]'}`}>
                      <div className="flex items-center gap-4">
                        <div className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 ${file ? 'bg-green-100' : 'bg-[#F5F0E3]'}`}>
                          {file ? <Check className="w-5 h-5 text-green-600" /> : <Icon className="w-5 h-5 text-[#92710A]" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-[#111827] font-medium text-sm font-body">{label} *</p>
                          <p className="text-[#4B5563] text-xs mt-0.5 font-body truncate">{file ? file.name : desc}</p>
                        </div>
                        <label className={`flex-shrink-0 px-4 py-2 rounded-full text-xs font-medium cursor-pointer transition-all inline-flex items-center gap-1.5 ${
                          file
                            ? 'bg-green-50 text-green-700 border border-green-200 hover:bg-green-100'
                            : 'lt-btn-gold'
                        }`}>
                          <Upload className="w-3 h-3" />
                          {file ? 'Remplacer' : 'Choisir'}
                          <input type="file" accept={accept} className="hidden" data-testid={`upload-${key}`}
                            onChange={(e) => { if (e.target.files[0]) setDocFiles(prev => ({ ...prev, [key]: e.target.files[0] })); }} />
                        </label>
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>

            {/* === SECTION 3: Plans (informatif) === */}
            <section>
              <h2 className="text-sm font-display font-medium text-[#111827] uppercase tracking-wider mb-1 pb-2 border-b border-[#E8E5DE]">
                Plans d'abonnement
              </h2>
              <p className="text-[#4B5563] text-xs mb-4 font-body">
                Vous choisirez votre plan après validation de votre dossier par notre équipe.
              </p>
              <div className="bg-[#FEF9E7] border border-[#F5E6A3] rounded-xl p-4 flex items-start gap-3">
                <CardIcon className="w-5 h-5 text-[#92710A] flex-shrink-0 mt-0.5" />
                <div className="text-xs text-[#6B5A0F] font-body leading-relaxed">
                  <p className="font-medium mb-1">3 formules hybrides : abonnement mensuel + commission par session</p>
                  <p>Starter · Pro ⭐ · Elite — le choix du plan se fera depuis votre dashboard coach une fois votre profil approuvé.</p>
                </div>
              </div>
            </section>

            {/* === SECTION 3b: Mode de paiement === */}
            <section>
              <h2 className="text-sm font-display font-medium text-[#111827] uppercase tracking-wider mb-1 pb-2 border-b border-[#E8E5DE]">
                Mode de paiement
              </h2>
              <p className="text-[#4B5563] text-xs mb-4 font-body">Comment souhaitez-vous recevoir vos paiements ?</p>
              <div className="space-y-3">
                {/* Bank Transfer */}
                <button type="button" data-testid="payment-bank-transfer"
                  onClick={() => setForm({ ...form, payment_method: 'bank_transfer' })}
                  className={`w-full p-4 rounded-xl border-2 text-left transition-all ${
                    form.payment_method === 'bank_transfer'
                      ? 'border-[#D4AF37] bg-[#FEF9E7]'
                      : 'border-[#E8E5DE] bg-white hover:border-[#DDD9D0]'
                  }`}>
                  <div className="flex items-center gap-3">
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                      form.payment_method === 'bank_transfer' ? 'border-[#D4AF37]' : 'border-[#DDD9D0]'
                    }`}>
                      {form.payment_method === 'bank_transfer' && <div className="w-2.5 h-2.5 rounded-full bg-[#D4AF37]" />}
                    </div>
                    <div>
                      <p className="text-[#111827] text-sm font-medium font-body">Virement bancaire</p>
                      <p className="text-[#4B5563] text-xs font-body">RIB + IBAN obligatoires</p>
                    </div>
                  </div>
                </button>
                {form.payment_method === 'bank_transfer' && (
                  <div className="ml-8 space-y-3 border-l-2 border-[#D4AF37]/20 pl-4">
                    <div>
                      <label className="text-sm font-medium text-[#374151] mb-1.5 block font-body">Titulaire du compte *</label>
                      <Input data-testid="payment-account-holder" value={form.account_holder}
                        onChange={(e) => setForm({ ...form, account_holder: e.target.value })}
                        className="bg-white border-[#DDD9D0] text-[#111827] rounded-xl" placeholder="Nom Prénom ou Raison sociale" />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-[#374151] mb-1.5 block font-body">IBAN *</label>
                      <Input data-testid="payment-iban" value={form.iban}
                        onChange={(e) => setForm({ ...form, iban: e.target.value.toUpperCase() })}
                        className="bg-white border-[#DDD9D0] text-[#111827] rounded-xl font-mono text-xs" placeholder="FR76 XXXX XXXX XXXX XXXX XXXX XXX" />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-[#374151] mb-1.5 block font-body">BIC (optionnel)</label>
                      <Input data-testid="payment-bic" value={form.bic}
                        onChange={(e) => setForm({ ...form, bic: e.target.value.toUpperCase() })}
                        className="bg-white border-[#DDD9D0] text-[#111827] rounded-xl font-mono text-xs" placeholder="BNPAFRPPXXX" />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-[#374151] mb-1.5 block font-body">RIB (document) *</label>
                      <div className={`flex items-center gap-3 p-3 rounded-xl border ${ribFile ? 'border-green-300 bg-green-50/30' : 'border-[#DDD9D0] bg-white'}`}>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs text-[#4B5563] truncate font-body">{ribFile ? ribFile.name : 'Aucun fichier sélectionné'}</p>
                        </div>
                        <label className={`flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-medium cursor-pointer ${ribFile ? 'bg-green-50 text-green-700 border border-green-200' : 'lt-btn-gold'}`}>
                          {ribFile ? 'Remplacer' : 'Joindre RIB'}
                          <input type="file" accept=".pdf,.jpg,.jpeg,.png" className="hidden" data-testid="upload-rib"
                            onChange={(e) => { if (e.target.files[0]) setRibFile(e.target.files[0]); }} />
                        </label>
                      </div>
                    </div>
                  </div>
                )}

                {/* PayPal */}
                <button type="button" data-testid="payment-paypal"
                  onClick={() => setForm({ ...form, payment_method: 'paypal' })}
                  className={`w-full p-4 rounded-xl border-2 text-left transition-all ${
                    form.payment_method === 'paypal'
                      ? 'border-[#D4AF37] bg-[#FEF9E7]'
                      : 'border-[#E8E5DE] bg-white hover:border-[#DDD9D0]'
                  }`}>
                  <div className="flex items-center gap-3">
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                      form.payment_method === 'paypal' ? 'border-[#D4AF37]' : 'border-[#DDD9D0]'
                    }`}>
                      {form.payment_method === 'paypal' && <div className="w-2.5 h-2.5 rounded-full bg-[#D4AF37]" />}
                    </div>
                    <div>
                      <p className="text-[#111827] text-sm font-medium font-body">PayPal</p>
                      <p className="text-[#4B5563] text-xs font-body">Adresse PayPal obligatoire</p>
                    </div>
                  </div>
                </button>
                {form.payment_method === 'paypal' && (
                  <div className="ml-8 border-l-2 border-[#D4AF37]/20 pl-4">
                    <label className="text-sm font-medium text-[#374151] mb-1.5 block font-body">Adresse PayPal *</label>
                    <Input data-testid="payment-paypal-email" value={form.paypal_email}
                      onChange={(e) => setForm({ ...form, paypal_email: e.target.value })}
                      className="bg-white border-[#DDD9D0] text-[#111827] rounded-xl" placeholder="votre.email@paypal.com" type="email" />
                  </div>
                )}
              </div>
            </section>

            {/* === SUBMIT === */}
            <div className="pt-2 pb-8">
              <button data-testid="submit-coach-onboarding" onClick={handleSubmit}
                disabled={submitting || !user || !isFormComplete}
                className="w-full py-4 lt-btn-gold font-display text-sm disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2">
                {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Création en cours...</> : <>Créer mon profil coach <ArrowRight className="w-4 h-4" /></>}
              </button>
              {!isFormComplete && (
                <p className="text-center text-[#4B5563] text-xs mt-3 font-body">
                  Remplissez tous les champs obligatoires (*), déposez vos 4 justificatifs et choisissez un mode de paiement.
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // === LANDING ===
  return (
    <div className="theme-light min-h-screen">
      <div className="border-b border-[#E8E5DE]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 pt-20 sm:pt-6 pb-2">
          <BackButton />
        </div>
      </div>

      {/* Hero */}
      <div className="relative">
        <div className="absolute inset-0 bg-gradient-to-b from-[#F5F0E3]/60 via-transparent to-transparent pointer-events-none" />
        <div className="max-w-5xl mx-auto px-4 sm:px-6 text-center pt-16 pb-14 relative">
          <p className="uppercase text-xs tracking-[0.25em] text-[#92710A] mb-4 font-body font-medium">Espace Coachs Coach DIA</p>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-display font-medium text-[#111827] tracking-tight mb-5 leading-tight">
            Proposez vos services<br />de coaching
          </h1>
          <p className="text-[#4B5563] text-base sm:text-lg max-w-2xl mx-auto leading-relaxed mb-8 font-body">
            Rejoignez une plateforme premium et accompagnez des managers en recherche de transformation.
          </p>
          <button data-testid="start-coach-onboarding"
            onClick={() => user ? setStep('form') : navigate('/login')}
            className="px-8 py-3.5 lt-btn-gold font-display text-sm inline-flex items-center gap-2">
            Devenir coach <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Benefits */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-14">
        <h2 className="text-base font-display font-medium text-[#111827] text-center mb-8">Pourquoi rejoindre Coach DIA ?</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {BENEFITS.map((b, i) => (
            <div key={i} className="bg-white border border-[#E8E5DE] rounded-2xl p-5 shadow-sm hover:-translate-y-1 hover:shadow-md transition-all duration-300">
              <div className="w-10 h-10 rounded-xl bg-[#F5F0E3] flex items-center justify-center mb-3">
                <b.icon className="w-5 h-5 text-[#92710A]" />
              </div>
              <h3 className="text-[#111827] font-display font-medium text-sm mb-1">{b.title}</h3>
              <p className="text-[#4B5563] text-xs leading-relaxed font-body">{b.desc}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Pricing */}
      <div className="max-w-3xl mx-auto px-4 sm:px-6 pb-28 sm:pb-16">
        <div className="bg-white border border-[#E8E5DE] rounded-2xl p-8 text-center shadow-sm">
          <p className="text-[#92710A] text-sm font-medium font-body mb-2">3 formules hybrides</p>
          <p className="text-[#92710A] text-3xl font-display font-semibold mb-1" data-testid="coach-plans-range">
            {plansRange.min && plansRange.max
              ? <>{plansRange.min}€ à {plansRange.max}€</>
              : <>—</>}
            <span className="text-base text-[#4B5563] font-normal">/mois</span>
          </p>
          <p className="text-[#4B5563] text-sm font-body">+ commission 10-20% · Starter · Pro ⭐ · Elite · sans engagement</p>
        </div>
      </div>
    </div>
  );
}
